import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { KpiCard } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import PipelineKanban from "@/components/PipelineKanban";
import { formatEUR, formatDate } from "@/lib/constants";
import {
  Users, FileText, Wallet, Send, Flame, PhoneCall, Trophy, MessageSquare, ChevronRight, Sparkles, LifeBuoy, Gauge, AlertTriangle, Rocket
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SolutionBadge, PriorityBadge, EstimateStatusBadge, HotLeadBadge } from "@/components/EnergyBadges";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const { user, company } = useAuth();

  const loadStats = async () => {
    try {
      const r = await api.get("/stats/dashboard");
      setStats(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStats(); }, []);

  const canEditClientOnboarding = user?.role !== "client_user";
  const needsClientOnboarding = company && company.onboarding_completed === false;
  const isAgencyAdminViewingClient = user?.role === "agency_admin" || user?.role === "platform_admin";

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          Dashboard
        </h1>
        <p className="mt-1 text-slate-600">Ihr Überblick über Leads, Angebote und Pipeline</p>
      </header>

      {/* Phase 4.3 — Mandanten-Onboarding-Hinweis */}
      {needsClientOnboarding && (
        <Card
          data-testid="client-onboarding-hint"
          className={`p-5 flex items-center justify-between gap-4 flex-wrap ${
            isAgencyAdminViewingClient ? "border-violet-200 bg-violet-50" : "border-orange-200 bg-orange-50"
          }`}
        >
          <div className="flex items-start gap-3">
            <Rocket className={`w-6 h-6 ${isAgencyAdminViewingClient ? "text-violet-600" : "text-orange-600"} mt-0.5`} />
            <div>
              <p className="font-bold text-slate-900">
                {isAgencyAdminViewingClient
                  ? "Dieser Mandant hat sein Angebotssetup noch nicht abgeschlossen."
                  : "Richten Sie Ihren Angebotsauftritt ein."}
              </p>
              <p className="text-sm text-slate-600 mt-0.5">
                {isAgencyAdminViewingClient
                  ? "Helfen Sie dem Mandanten beim Branding, Support-Daten und Angebotsdomain."
                  : "5 Schritte — Logo, Farben, Support, E-Mail-CI und Angebotsdomain. ~3 Minuten."}
              </p>
            </div>
          </div>
          {canEditClientOnboarding && (
            <Button
              data-testid="start-client-onboarding-btn"
              onClick={() => nav("/onboarding/client")}
              className={isAgencyAdminViewingClient ? "bg-violet-600 hover:bg-violet-700 text-white" : "bg-orange-600 hover:bg-orange-700 text-white"}
            >
              Onboarding starten <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </Card>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(6)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-32" />)}
        </div>
      ) : (
        <>
          <PipelineKanban pipeline={stats.pipeline} totalValue={stats.pipeline_total_value} />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <KpiCard testid="kpi-hot" label="Hot Leads" value={stats.hot_leads} icon={Flame} accent="orange" />
            <KpiCard testid="kpi-callbacks" label="Rückruftermine" value={stats.callbacks} icon={PhoneCall} accent="amber" />
            <KpiCard testid="kpi-response-rate" label="Antwortquote" value={`${stats.response_rate}%`} icon={MessageSquare} accent="slate" />
            <KpiCard testid="kpi-won-value" label="Gewonnener Umsatz" value={formatEUR(stats.won_value)} icon={Trophy} accent="emerald" />
            <KpiCard testid="kpi-recovered-revenue" label="Geretteter Umsatz" value={formatEUR(stats.recovered_revenue || 0)} icon={LifeBuoy} accent="emerald" />
            <KpiCard testid="kpi-reactivated-value" label="Reaktivierter Wert" value={formatEUR(stats.reactivated_offer_value || 0)} icon={Sparkles} accent="orange" />
          </div>
        </>
      )}

      {!loading && stats?.lead_usage && (() => {
        const u = stats.lead_usage;
        const pct = u.monthly_lead_limit > 0 ? Math.min(100, Math.round((u.processed_leads / u.monthly_lead_limit) * 100)) : 0;
        const limitReached = u.processed_leads >= u.monthly_lead_limit;
        const nearLimit = pct >= 80 && !limitReached;
        const barColor = limitReached ? "bg-red-500" : nearLimit ? "bg-amber-500" : "bg-emerald-500";
        return (
          <Card data-testid="lead-usage-card" className="border-slate-200 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-50 text-slate-700"><Gauge className="w-5 h-5" /></div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Leadvolumen</p>
                  <p className="text-lg font-bold text-slate-900 mt-0.5" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="lead-usage-count">
                    {u.processed_leads} von {u.monthly_lead_limit} Leads verarbeitet
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Aktueller Plan: <span className="font-semibold text-slate-700">{u.plan_name}</span> · Abrechnungsmonat {u.billing_month}</p>
                </div>
              </div>
              <button
                data-testid="lead-usage-upgrade-link"
                onClick={() => nav("/settings")}
                className="text-sm font-medium text-orange-600 hover:text-orange-700"
              >
                Plan & Verbrauch verwalten →
              </button>
            </div>
            <div className="mt-4 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <div data-testid="lead-usage-bar" className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>
            {limitReached && (
              <div data-testid="lead-usage-warning-limit" className="mt-3 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Leadlimit erreicht. Upgrade erforderlich, um weitere Leads zu verarbeiten.{u.recommended_next_plan ? ` Empfehlung: Upgrade auf ${u.recommended_next_plan}.` : ""}</span>
              </div>
            )}
            {nearLimit && (
              <div data-testid="lead-usage-warning-near" className="mt-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Sie nähern sich Ihrem monatlichen Leadlimit.</span>
              </div>
            )}
          </Card>
        );
      })()}

      {!loading && stats?.new_hot_leads?.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/40" data-testid="new-hot-leads-card">
          <div className="px-6 py-4 border-b border-orange-200 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-slate-900">Sofort-Leads: bitte umgehend melden</h2>
            <span className="text-xs text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">{stats.new_hot_leads.length}</span>
          </div>
          <ul className="divide-y divide-orange-100">
            {stats.new_hot_leads.map((h) => (
              <li key={h.lead_id} className="px-6 py-3 flex items-center justify-between gap-4 hover:bg-orange-50">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900 truncate" data-testid={`hot-lead-name-${h.lead_id}`}>{h.name || "—"}</p>
                    <SolutionBadge value={h.solution_type} testid={`hot-lead-solution-${h.lead_id}`} />
                    <PriorityBadge value={h.priority} testid={`hot-lead-priority-${h.lead_id}`} />
                  </div>
                  <p className="text-xs text-slate-600 truncate mt-0.5">{h.reason} · {formatDate(h.since)}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-sm font-semibold tabular-nums text-slate-900">{formatEUR(h.value)}</span>
                  <button
                    data-testid={`hot-lead-details-${h.lead_id}`}
                    onClick={() => nav(`/leads/${h.lead_id}`)}
                    className="inline-flex items-center text-sm text-orange-700 hover:text-orange-800 font-medium"
                  >
                    Details <ChevronRight className="w-4 h-4 ml-0.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Aktuelle Leads</h2>
          <button
            data-testid="view-all-leads"
            onClick={() => nav("/leads")}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium"
          >
            Alle Leads →
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-6 py-3 font-semibold">Kunde</th>
                <th className="px-6 py-3 font-semibold text-right">Wert</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold">Letzter Kontakt</th>
                <th className="px-6 py-3 font-semibold">Nächster Schritt</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!loading && stats?.recent_leads?.map((l) => (
                <tr key={l.lead_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {l.is_hot && <Flame className="w-4 h-4 text-orange-500" />}
                      <div>
                        <span className="font-medium text-slate-900">{l.name || "—"}</span>
                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                          <SolutionBadge value={l.solution_type} testid={`dash-solution-${l.lead_id}`} />
                          <PriorityBadge value={l.priority} testid={`dash-priority-${l.lead_id}`} />
                          {l.estimate_status && <EstimateStatusBadge value={l.estimate_status} testid={`dash-estimate-${l.lead_id}`} />}
                          {l.is_hot && <HotLeadBadge reason={l.hot_lead_reason} testid={`dash-hot-${l.lead_id}`} />}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums text-slate-700">{formatEUR(l.value)}</td>
                  <td className="px-6 py-4"><StatusBadge status={l.status} /></td>
                  <td className="px-6 py-4 text-sm text-slate-600">{formatDate(l.last_contact)}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{l.next_action || "—"}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      data-testid={`view-lead-${l.lead_id}`}
                      onClick={() => nav(`/leads/${l.lead_id}`)}
                      className="inline-flex items-center text-sm text-slate-700 hover:text-orange-600 font-medium"
                    >
                      Details <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && (!stats?.recent_leads || stats.recent_leads.length === 0) && (
                <tr><td colSpan="6" className="px-6 py-10 text-center text-slate-500">Noch keine Leads vorhanden.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
